import { InformationCircleIcon } from "@heroicons/react/outline";
import {
  capitalize,
  get,
  isEmpty,
  keys,
  map,
  pickBy,
  sumBy,
  uniq,
  values
} from "lodash";
import Tooltip from "rc-tooltip";
import React, { useMemo } from "react";
import CenteredLoading from "../../../../components/CenteredLoading";
import DonutChart, {
  DonutSection
} from "../../../../components/charts/DonutChart";
import { useHotspotMakers } from "../../../../context/hotspotMakers";
import { useStats } from "../../../../context/stats";
import { HistogramData } from "../../../../context/stats/StatsProvider";
import { locale } from "../../../../utils/dates";
import {
  classificationBoundaries,
  classificationColorMap
} from "../../../../utils/hotspotUtils";

interface Props {}

const HotspotStatistics: React.FC<Props> = () => {
  const { stats, isLoading } = useStats();
  const { hotspotMakersById } = useHotspotMakers();

  const [onlineHotspots, activeHotspots, onlineActiveHotspots] = useMemo(
    () => [
      sumBy(values(hotspotMakersById), "all_hotspots.online"),
      sumBy(values(hotspotMakersById), "installed_hotspots.total"),
      sumBy(values(hotspotMakersById), "installed_hotspots.online")
    ],
    [hotspotMakersById]
  );

  const filteredStatistics: StatisticListItem[] = useMemo(() => {
    if (!stats) return [];

    const transmitScalesChartData: DonutSection[] = map(
      pickBy(stats.histograms.transmit_scale, ({ count }) => count > 0),
      ({ count }: HistogramData, key: string) => ({
        key,
        name: `${capitalize(key)}: ${uniq(classificationBoundaries[key]).join(
          "-"
        )}`,
        value: count,
        displayValues: [`${count.toLocaleString(undefined)} hotspots`],
        percentage:
          (count * 100) /
          sumBy(values(stats.histograms.transmit_scale), "count"),
        color: get(classificationColorMap, key)
      })
    ) as any[];

    const witnessScoresChartData: DonutSection[] = map(
      pickBy(stats.histograms.witness_score, ({ count }) => count > 0),
      ({ count }: HistogramData, key: string) => ({
        key,
        name: `${capitalize(key)}: ${uniq(classificationBoundaries[key]).join(
          "-"
        )}`,
        value: count,
        displayValues: [`${count.toLocaleString(undefined)} hotspots`],
        percentage:
          (count * 100) /
          sumBy(values(stats.histograms.witness_score), "count"),
        color: get(classificationColorMap, key)
      })
    ) as any[];

    return [
      {
        title: "Blocks",
        key: "blocks",
        value: formatNumber(stats.blocks)
      },
      {
        title: "Hotspots",
        key: "hotspots",
        value: formatNumber(stats.hotspots),
        description: `${((onlineHotspots * 100) / stats.hotspots).toFixed(
          0
        )}% active`,
      },
      {
        title: "Activated hotspots",
        key: "activatedHotspots",
        value: formatNumber(activeHotspots),
        description: `${((onlineActiveHotspots * 100) / activeHotspots).toFixed(
          0
        )}% active`,
        tooltip:
          "An 'activated hotspot' is a hotspot that has already participated in proof of coverage activities."
      },
      {
        title: "Makers",
        key: "makers",
        value: formatNumber(keys(hotspotMakersById).length)
      },
      {
        title: "Countries",
        key: "countries",
        value: formatNumber(stats.countries)
      },
      {
        title: "Cities",
        key: "cities",
        value: formatNumber(stats.cities)
      },
      {
        title: "Witness Scores",
        key: "witnessScores",
        alternativeContent: (
          <div className="mt-4">
            <DonutChart
              className="mx-auto"
              data={witnessScoresChartData}
              sizeInPx={240}
            />
          </div>
        )
      },
      {
        title: "Transmit Scales",
        key: "transmitScales",
        alternativeContent: (
          <div className="mt-4">
            <DonutChart
              className="mx-auto"
              data={transmitScalesChartData}
              sizeInPx={240}
            />
          </div>
        )
      }
    ];
  }, [
    activeHotspots,
    hotspotMakersById,
    onlineActiveHotspots,
    onlineHotspots,
    stats
  ]);

  if (isLoading || isEmpty(hotspotMakersById)) {
    return <CenteredLoading className="h-full" />;
  }

  return (
    <nav
      className="flex-1 min-h-0 relative overflow-y-auto"
      aria-label="Statistics"
    >
      <ul className="relative z-0 divide-y divide-gray-200 dark:divide-gray-500 h-full">
        <div className="flex flex-col w-full pb-2">
          {filteredStatistics.map(renderStatisticCard)}
        </div>
      </ul>
    </nav>
  );
};

export default HotspotStatistics;

//
// Utils
//

interface StatisticListItem {
  key: string;
  title: string;
  value?: string | number | undefined;
  description?: JSX.Element | string | undefined;
  alternativeContent?: JSX.Element | undefined;
  tooltip?: string;
}

const formatNumber = (value: number) => Intl.NumberFormat(locale).format(value);

const renderStatisticCard = ({
  title,
  key,
  value,
  description,
  alternativeContent,
  tooltip
}: StatisticListItem) => (
  <div
    key={key}
    className={
      "rounded-md mx-4 my-2 px-4 py-5 flex items-center dark:ring-white bg-gray-200 dark:bg-gray-700 first:mt-2"
    }
  >
    <div className="flex flex-col w-full">
      <p className="text-sm text-gray-500 truncate dark:text-gray-200">
        <div className="flex items-center">
          <span className="text-sm mr-1 text-gray-500 truncate dark:text-gray-200">
            {title}
          </span>
          {tooltip && (
            <Tooltip overlay={tooltip}>
              <InformationCircleIcon className="mt-0.5 h-4 w-4 text-gray-400 dark:text-white" />
            </Tooltip>
          )}
        </div>
      </p>
      {(value || description) && (
        <div className="flex flex-row">
          {value && (
            <span className="text-2xl mr-2 font-medium text-gray-800 truncate dark:text-white">
              {value}
            </span>
          )}
          {description && (
            <p className="text-sm truncate mt-3 text-gray-600 dark:text-gray-200">
              {description}
            </p>
          )}
        </div>
      )}
      {alternativeContent}
    </div>
  </div>
);
